import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { UseCheckboxSelectionProps, UseCheckboxSelectionReturn } from './types'

export function useCheckboxSelection<TData>({
	data,
	getRowId,
	initialSelection = [],
	onSelectionChange,
	onSelectionStateChange,
	selectableRowFilter,
	allData,
	totalCount,
	fetchAllSelectableIds,
}: UseCheckboxSelectionProps<TData>): UseCheckboxSelectionReturn<TData> {
	const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set(initialSelection))
	const [deselectedRowIds, setDeselectedRowIds] = useState<Set<string>>(new Set())
	const [selectedData, setSelectedData] = useState<TData[]>([])
	const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
	const [lastCheckedRowId, setLastCheckedRowId] = useState<string | null>(null)
	const [isSelectingAll, setIsSelectingAll] = useState(false)
	// Id count from the last server select-all; null when selection is local-only
	const [serverSelectAllCount, setServerSelectAllCount] = useState<number | null>(null)

	// Server select-all: request id discards stale resolutions, controller aborts in-flight fetch,
	// ref mirrors isSelectingAll for re-entry guards
	const selectAllRequestRef = useRef(0)
	const selectAllAbortRef = useRef<AbortController | null>(null)
	const isSelectingAllRef = useRef(false)

	// Bumped on every mutation — cheap selection identity, avoids joining 100k+ ids
	const selectionVersionRef = useRef(0)

	// ref to track if we have synced
	const hasSyncedRef = useRef(false)

	// Live copy of the current selection, kept in a ref.
	// Why: two checkbox clicks can fire before React re-renders. If each handler
	// read the selection from its own closure, both would start from the same old
	// Set and the second would overwrite the first — a "lost" click. Reading and
	// writing this ref instead means every click sees the latest selection.
	const selectionRef = useRef<{ ids: Set<string>; data: TData[] }>({ ids: selectedRowIds, data: selectedData })
	useEffect(() => {
		selectionRef.current = { ids: selectedRowIds, data: selectedData }
	}, [selectedRowIds, selectedData])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <only reset anchor when data size changes (pagination/filtering)>
	useEffect(() => {
		setLastSelectedIndex(null)
	}, [data.length])

	useEffect(() => {
		if (!hasSyncedRef.current && data.length > 0 && initialSelection.length > 0) {
			// Set lookup — initial selection can hold 100k+ ids
			const initialIdSet = new Set(initialSelection)
			const syncedData = data.filter(item => initialIdSet.has(getRowId(item)))
			setSelectedData(syncedData)
			hasSyncedRef.current = true
		}
	}, [data, getRowId, initialSelection])

	const effectiveTotalCount = useMemo(() => {
		if (totalCount !== undefined) {
			return totalCount // Use if provided (best for performance)
		}
		if (allData && allData.length > 0) {
			return allData.length // Auto-calculate from allData
		}
		return data.length // Fallback to current page
	}, [totalCount, allData, data])

	const selectableRows = useMemo(() => {
		const dataToUse = allData && allData.length > data.length ? allData : data
		return selectableRowFilter ? dataToUse.filter(selectableRowFilter) : dataToUse
	}, [allData, data, selectableRowFilter])

	const hasSelectableRows = selectableRows.length > 0

	// Distinguishes "every matching row" from "every loaded row" — the fallback path leaves the
	// count null, and callers acting on the whole match set must not treat that as a full selection.
	const isServerSelectAll = serverSelectAllCount !== null

	// Keep loaded page rows selected when navigating pages during a server select-all
	useEffect(() => {
		if (isServerSelectAll && data.length > 0) {
			const loadedIds = selectableRows.map(row => getRowId(row)).filter(id => !deselectedRowIds.has(id))
			const currentIds = selectionRef.current.ids
			let hasMissing = false
			for (const id of loadedIds) {
				if (!currentIds.has(id)) {
					hasMissing = true
					break
				}
			}
			if (hasMissing) {
				const nextIds = new Set(currentIds)
				for (const id of loadedIds) {
					nextIds.add(id)
				}
				const nextData = selectableRows.filter(
					row => nextIds.has(getRowId(row)) && !deselectedRowIds.has(getRowId(row)),
				)
				selectionRef.current = { ids: nextIds, data: nextData }
				selectionVersionRef.current += 1
				setSelectedRowIds(nextIds)
				setSelectedData(nextData)
			}
		}
	}, [data, isServerSelectAll, selectableRows, getRowId, deselectedRowIds])

	const selectedCount = isServerSelectAll
		? Math.max(0, (serverSelectAllCount ?? 0) - deselectedRowIds.size)
		: selectedRowIds.size
	const selectedRowIdsArray = useMemo(() => [...selectedRowIds], [selectedRowIds])
	const deselectedRowIdsArray = useMemo(() => [...deselectedRowIds], [deselectedRowIds])

	const isAllSelected = useMemo(() => {
		if (selectableRows.length === 0) {
			return false
		}
		if (isServerSelectAll) {
			return deselectedRowIds.size === 0 && (serverSelectAllCount ?? 0) > 0
		}
		const targetCount = selectableRowFilter ? selectableRows.length : effectiveTotalCount
		return selectedRowIds.size === targetCount
	}, [
		selectableRows.length,
		isServerSelectAll,
		deselectedRowIds.size,
		serverSelectAllCount,
		selectableRowFilter,
		effectiveTotalCount,
		selectedRowIds.size,
	])

	const isPartiallySelected = useMemo(() => {
		if (isServerSelectAll) {
			return deselectedRowIds.size > 0 && selectedCount > 0
		}
		return selectedCount > 0 && !isAllSelected
	}, [isServerSelectAll, deselectedRowIds.size, selectedCount, isAllSelected])

	const isSelected = useCallback(
		(id: string): boolean => {
			if (isServerSelectAll) {
				return !deselectedRowIds.has(id)
			}
			return selectedRowIds.has(id)
		},
		[isServerSelectAll, deselectedRowIds, selectedRowIds],
	)

	const isRowSelectable = useCallback(
		(row: TData): boolean => {
			return selectableRowFilter ? selectableRowFilter(row) : true // if filter is provided it applies, else all rows are selectable so returns true
		},
		[selectableRowFilter],
	)

	// Range selection
	const rangeSelect = useCallback(
		(startIndex: number, endIndex: number, shouldSelect: boolean) => {
			const prevIds = selectionRef.current.ids
			const prevData = selectionRef.current.data
			const newSelectedRowIds = new Set(prevIds)
			const rangesToProcess: TData[] = []
			let lastProcessedId: string | null = null

			// Process all items in the range
			for (let i = startIndex; i <= endIndex; i++) {
				if (i >= 0 && i < data.length) {
					const item = data[i]
					const id = getRowId(item)

					// Only process selectable rows
					if (isRowSelectable(item)) {
						if (shouldSelect) {
							newSelectedRowIds.add(id)
							if (!prevData.some(selectedItem => getRowId(selectedItem) === id)) {
								rangesToProcess.push(item)
							}
							lastProcessedId = id
						} else {
							newSelectedRowIds.delete(id)
							lastProcessedId = id
						}
					}
				}
			}

			// Update selected data
			const newSelectedData = shouldSelect
				? [...prevData, ...rangesToProcess]
				: prevData.filter(item => {
						const id = getRowId(item)
						return newSelectedRowIds.has(id)
					})

			selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
			selectionVersionRef.current += 1
			setSelectedRowIds(newSelectedRowIds)
			setSelectedData(newSelectedData)

			// Track last checked row based on range interaction - only on selection
			if (lastProcessedId && shouldSelect) {
				setLastCheckedRowId(lastProcessedId)
			} else if (lastProcessedId && !shouldSelect) {
				setLastCheckedRowId(null)
			}

			// Notify change
			if (onSelectionChange) {
				const action = shouldSelect ? 'selectRange' : 'deselectRange'
				onSelectionChange([...newSelectedRowIds], newSelectedData, action)
			}
		},
		[data, getRowId, isRowSelectable, onSelectionChange],
	)

	const toggleRow = useCallback(
		(id: string, data: TData, index?: number, shiftPressed = false) => {
			const prevIds = selectionRef.current.ids
			const prevData = selectionRef.current.data

			if (isServerSelectAll) {
				const isCurrentlySelected = !deselectedRowIds.has(id)
				const nextDeselected = new Set(deselectedRowIds)
				if (isCurrentlySelected) {
					nextDeselected.add(id)
				} else {
					nextDeselected.delete(id)
				}
				setDeselectedRowIds(nextDeselected)
				selectionVersionRef.current += 1

				const nextSelectedData = isCurrentlySelected
					? prevData.filter(item => getRowId(item) !== id)
					: [...prevData, data]
				selectionRef.current = { ids: prevIds, data: nextSelectedData }
				setSelectedData(nextSelectedData)

				if (isCurrentlySelected) {
					setLastCheckedRowId(null)
				} else {
					setLastCheckedRowId(id)
				}
				if (index !== undefined) {
					setLastSelectedIndex(index)
				}
				if (onSelectionChange) {
					const action = isCurrentlySelected ? 'deselect' : 'select'
					onSelectionChange([...prevIds], nextSelectedData, action)
				}
				return
			}

			if (shiftPressed && lastSelectedIndex !== null && index !== undefined) {
				const startIndex = Math.min(lastSelectedIndex, index)
				const endIndex = Math.max(lastSelectedIndex, index)

				// select/deselect based on clicked item's current state
				const shouldSelect = !prevIds.has(id)
				rangeSelect(startIndex, endIndex, shouldSelect)

				// not updating lastSelectedIndex for range selections (anchor point only on normal click , not during range operations)
				return
			}

			// Normal toggle behavior
			const isCurrentlySelected = prevIds.has(id)

			const newSelectedRowIds = new Set(prevIds)
			if (isCurrentlySelected) {
				newSelectedRowIds.delete(id)
			} else {
				newSelectedRowIds.add(id)
			}

			const newSelectedData = isCurrentlySelected
				? prevData.filter(item => getRowId(item) !== id) // deselects if currently selected
				: [...prevData, data] // selects if not currently selected

			// Updating state
			selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
			selectionVersionRef.current += 1
			setSelectedRowIds(newSelectedRowIds)
			setSelectedData(newSelectedData)

			// Track last checked row for contextual menu positioning - only on selection
			if (isCurrentlySelected) {
				setLastCheckedRowId(null)
			} else {
				setLastCheckedRowId(id)
			}

			// Update anchor point for future range selections
			if (index !== undefined) {
				setLastSelectedIndex(index)
			}

			// Calling event handler
			if (onSelectionChange) {
				const action = isCurrentlySelected ? 'deselect' : 'select'
				onSelectionChange([...newSelectedRowIds], newSelectedData, action) //convert Set to array for consistency
			}
		},
		[isServerSelectAll, deselectedRowIds, getRowId, onSelectionChange, lastSelectedIndex, rangeSelect],
	)

	const selectRow = useCallback(
		(id: string, data: TData) => {
			if (isServerSelectAll) {
				if (deselectedRowIds.has(id)) {
					const nextDeselected = new Set(deselectedRowIds)
					nextDeselected.delete(id)
					setDeselectedRowIds(nextDeselected)
					selectionVersionRef.current += 1
					const nextSelectedData = [...selectionRef.current.data, data]
					selectionRef.current = { ids: selectionRef.current.ids, data: nextSelectedData }
					setSelectedData(nextSelectedData)
					setLastCheckedRowId(id)
					if (onSelectionChange) {
						onSelectionChange([...selectionRef.current.ids], nextSelectedData, 'select')
					}
				}
				return
			}
			const prevIds = selectionRef.current.ids
			if (!prevIds.has(id)) {
				const newSelectedRowIds = new Set(prevIds) //creating a new Set to avoid mutating state directly
				newSelectedRowIds.add(id)
				const newSelectedData = [...selectionRef.current.data, data]

				selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
				selectionVersionRef.current += 1
				setSelectedRowIds(newSelectedRowIds)
				setSelectedData(newSelectedData)

				// Track last checked row for programmatic selection
				setLastCheckedRowId(id)

				if (onSelectionChange) {
					onSelectionChange([...newSelectedRowIds], newSelectedData, 'select')
				}
			}
		},
		[isServerSelectAll, deselectedRowIds, onSelectionChange],
	)

	const deselectRow = useCallback(
		(id: string) => {
			if (isServerSelectAll) {
				if (!deselectedRowIds.has(id)) {
					const nextDeselected = new Set(deselectedRowIds)
					nextDeselected.add(id)
					setDeselectedRowIds(nextDeselected)
					selectionVersionRef.current += 1
					const nextSelectedData = selectionRef.current.data.filter(item => getRowId(item) !== id)
					selectionRef.current = { ids: selectionRef.current.ids, data: nextSelectedData }
					setSelectedData(nextSelectedData)
					setLastCheckedRowId(null)
					if (onSelectionChange) {
						onSelectionChange([...selectionRef.current.ids], nextSelectedData, 'deselect')
					}
				}
				return
			}
			const prevIds = selectionRef.current.ids
			if (prevIds.has(id)) {
				// Create a new Set and remove the ID
				const newSelectedRowIds = new Set(prevIds)
				newSelectedRowIds.delete(id)

				const newSelectedData = selectionRef.current.data.filter(item => getRowId(item) !== id)

				selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
				selectionVersionRef.current += 1
				setSelectedRowIds(newSelectedRowIds)
				setSelectedData(newSelectedData)

				// Clear last checked row ID on deselection
				setLastCheckedRowId(null)

				if (onSelectionChange) {
					onSelectionChange([...newSelectedRowIds], newSelectedData, 'deselect')
				}
			}
		},
		[isServerSelectAll, deselectedRowIds, getRowId, onSelectionChange],
	)

	const replaceSelection = useCallback(
		(id: string, data: TData) => {
			const newSelectedRowIds = new Set<string>([id])
			const newSelectedData = [data]

			selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
			selectionVersionRef.current += 1
			setDeselectedRowIds(new Set())
			setServerSelectAllCount(null)
			setSelectedRowIds(newSelectedRowIds)
			setSelectedData(newSelectedData)

			// Track last checked row when replacing selection
			setLastCheckedRowId(id)

			if (onSelectionChange) {
				onSelectionChange([id], newSelectedData, 'select') // Using 'select' action as we end up with a selection
			}
		},
		[onSelectionChange],
	)

	const selectAll = useCallback(() => {
		// Selects loaded rows only — default path, and the fallback when the server fetch fails
		const selectLoadedRows = () => {
			const allSelectableIds = selectableRows.map(row => getRowId(row))
			const newSelectedRowIds = new Set(allSelectableIds)

			selectionRef.current = { ids: newSelectedRowIds, data: selectableRows }
			selectionVersionRef.current += 1
			setDeselectedRowIds(new Set())
			setServerSelectAllCount(null)
			setSelectedRowIds(newSelectedRowIds)
			setSelectedData(selectableRows)
			setLastSelectedIndex(null) // Reset anchor
			setLastCheckedRowId(null) // Use header checkbox position when all are selected

			if (onSelectionChange) {
				onSelectionChange([...newSelectedRowIds], selectableRows, 'selectAll')
			}
		}

		if (!fetchAllSelectableIds) {
			selectLoadedRows()
			return
		}

		// Fetch already in flight — ignore rapid re-clicks
		if (isSelectingAllRef.current) {
			return
		}

		const requestId = ++selectAllRequestRef.current
		selectAllAbortRef.current?.abort()
		const controller = new AbortController()
		selectAllAbortRef.current = controller
		isSelectingAllRef.current = true
		setIsSelectingAll(true)

		fetchAllSelectableIds(controller.signal)
			.then(result => {
				if (requestId !== selectAllRequestRef.current) {
					return // superseded by a newer select-all or a clear
				}
				if (result === null) {
					selectLoadedRows()
					return
				}
				const ids = Array.isArray(result) ? result : result.ids
				const totalMatchCount = Array.isArray(result) ? result.length : result.count
				const loadedIds = selectableRows.map(row => getRowId(row))
				const newSelectedRowIds = new Set([...loadedIds, ...ids])
				// Server checks visibility only — drop ids the client-side row gate rejects
				if (selectableRowFilter) {
					const dataToUse = allData && allData.length > data.length ? allData : data
					for (const row of dataToUse) {
						if (!selectableRowFilter(row)) {
							newSelectedRowIds.delete(getRowId(row))
						}
					}
				}
				// Only loaded rows have data objects; unloaded matches are id-only
				const newSelectedData = selectableRows.filter(row => newSelectedRowIds.has(getRowId(row)))

				selectionRef.current = { ids: newSelectedRowIds, data: newSelectedData }
				selectionVersionRef.current += 1
				setDeselectedRowIds(new Set())
				setServerSelectAllCount(totalMatchCount)
				setSelectedRowIds(newSelectedRowIds)
				setSelectedData(newSelectedData)
				setLastSelectedIndex(null)
				setLastCheckedRowId(null)

				if (onSelectionChange) {
					onSelectionChange([...newSelectedRowIds], newSelectedData, 'selectAll')
				}
			})
			.catch((error: unknown) => {
				if (requestId !== selectAllRequestRef.current) {
					return // aborted by a newer request or a clear — nothing to do
				}
				console.error('Select-all id fetch failed, falling back to loaded rows:', error)
				selectLoadedRows()
			})
			.finally(() => {
				if (requestId === selectAllRequestRef.current) {
					isSelectingAllRef.current = false
					setIsSelectingAll(false)
				}
			})
	}, [selectableRows, getRowId, onSelectionChange, fetchAllSelectableIds, selectableRowFilter, allData, data])

	const deselectAll = useCallback(() => {
		// Abort in-flight server select-all so it can't resurrect the selection
		selectAllRequestRef.current += 1
		selectAllAbortRef.current?.abort()
		isSelectingAllRef.current = false
		setIsSelectingAll(false)

		const emptySet = new Set<string>()
		selectionRef.current = { ids: emptySet, data: [] }
		selectionVersionRef.current += 1
		setDeselectedRowIds(new Set())
		setServerSelectAllCount(null)
		setSelectedRowIds(emptySet) // Empty Set - no selections
		setSelectedData([])
		setLastSelectedIndex(null) // Reset anchor
		setLastCheckedRowId(null)

		if (onSelectionChange) {
			onSelectionChange([], [], 'deselectAll') // Notify parent with empty arrays
		}
	}, [onSelectionChange])

	const toggleAll = useCallback(() => {
		return isAllSelected ? deselectAll() : selectAll()
	}, [isAllSelected, selectAll, deselectAll])

	const clearSelection = deselectAll

	// shortcut to select all (Ctrl+A)
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
				event.preventDefault() // to prevent browser deafult behaviour from selecting full page text
				selectAll()
			}
		},
		[selectAll],
	)

	const functionsRef = useRef({
		isSelected,
		isRowSelectable,
		toggleRow,
		selectRow,
		deselectRow,
		selectAll,
		deselectAll,
		toggleAll,
		clearSelection,
		rangeSelect,
		replaceSelection,
	})

	// Updating refs whenever functions change
	useEffect(() => {
		functionsRef.current = {
			isSelected,
			isRowSelectable,
			toggleRow,
			selectRow,
			deselectRow,
			selectAll,
			deselectAll,
			toggleAll,
			clearSelection,
			rangeSelect,
			replaceSelection,
		}
	}, [
		isSelected,
		isRowSelectable,
		toggleRow,
		selectRow,
		deselectRow,
		selectAll,
		deselectAll,
		toggleAll,
		clearSelection,
		rangeSelect,
		replaceSelection,
	])

	// Track previous selection to avoid infinite loops
	const prevSelectionRef = useRef<string>('')

	useEffect(() => {
		if (onSelectionStateChange) {
			// key to uniquely identify current selection state; version replaces the id join (slow at 100k+ ids)
			const selectionKey = `${selectionVersionRef.current}-${selectedCount}-${deselectedRowIds.size}-${isAllSelected}-${isPartiallySelected}-${isSelectingAll}-${isServerSelectAll}-${lastCheckedRowId ?? ''}`

			if (prevSelectionRef.current !== selectionKey) {
				prevSelectionRef.current = selectionKey

				const state: UseCheckboxSelectionReturn<TData> = {
					selectedRowIds: selectedRowIdsArray,
					deselectedRowIds: deselectedRowIdsArray,
					selectedData,
					selectedCount,
					isAllSelected,
					isPartiallySelected,
					hasSelectableRows,
					isSelectingAll,
					isServerSelectAll,
					lastCheckedRowId,
					...functionsRef.current,
				}
				onSelectionStateChange(state)
			}
		}
	}, [
		selectedRowIdsArray,
		deselectedRowIdsArray,
		deselectedRowIds.size,
		selectedData,
		selectedCount,
		isAllSelected,
		isPartiallySelected,
		hasSelectableRows,
		isSelectingAll,
		isServerSelectAll,
		lastCheckedRowId,
		onSelectionStateChange,
	])

	// keyboard event listener
	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [handleKeyDown])

	// Abort any in-flight server select-all on unmount
	useEffect(() => {
		return () => {
			selectAllRequestRef.current += 1
			selectAllAbortRef.current?.abort()
		}
	}, [])

	return {
		selectedRowIds: selectedRowIdsArray,
		deselectedRowIds: deselectedRowIdsArray,
		selectedData,
		lastCheckedRowId,

		selectedCount,
		isAllSelected,
		isPartiallySelected,
		hasSelectableRows,
		isSelectingAll,
		isServerSelectAll,

		isSelected,
		isRowSelectable,
		toggleRow,
		selectRow,
		deselectRow,
		selectAll,
		deselectAll,
		toggleAll,
		clearSelection,
		rangeSelect,
		replaceSelection,
	}
}
